<script lang="ts" setup>
import Input from "@/components/form/Input.vue"
import Field from "@/components/form/Field.vue"
import Select from "@/components/form/Select.vue"
import Textarea from "@/components/form/Textarea.vue"
import Button from "@/components/form/Button.vue"
import LabelRequiredFields from "@/components/form/LabelRequiredFields.vue"
import Alert from "@/components/ui/Alert.vue"
import ModalIcon from "@/templates/operations/assets/controls/ModalIcon.vue"
import * as yup from "yup"
import {
  ChevronRightIcon,
  PaperClipIcon,
  TrashIcon
} from "@heroicons/vue/outline"
import { useSupport } from "@/stores/support"
import { useSystemFeedback } from "@/stores/systemFeedback"
import { useModal } from "@/stores/modal"
import { useField, useForm, useIsFormValid, useResetForm } from "vee-validate"
import { useI18n } from "vue-i18n"
import { h, ref, computed } from "vue"
import type { MixedSchema } from "yup/lib/mixed"
import type { ModalProps } from "@/types/modal"

const { t } = useI18n()
const systemFeedback = useSystemFeedback()
const modal = useModal()
const requestError = computed(() => systemFeedback.feedback)
const support = useSupport()
const attachment = ref()
const selectedFiles = ref<File[]>([])
const resetForm = useResetForm()

const subjectValues = computed(() => [
  {
    name: t("support.placeholderSubject"),
    value: "",
    isSelected: true
  },
  {
    name: t("support.fieldsSubject.doubt"),
    value: "Dúvida",
    isSelected: false
  },
  {
    name: t("support.fieldsSubject.problem"),
    value: "Problema",
    isSelected: false
  },
  {
    name: t("support.fieldsSubject.solicitation"),
    value: "Solicitação",
    isSelected: false
  },
  {
    name: t("support.fieldsSubject.support"),
    value: "Suporte Técnico",
    isSelected: false
  }
])

const modalStopOperation = computed(() => {
  return {
    icon: {
      name: h(ModalIcon, { iconName: "paper-airplane.svg" })
    },
    button: {
      confirmation: {
        text: "Ok",
        click: async () => {
          resetForm()
          selectedFiles.value = []
          modal.closeModal()
        }
      }
    },
    hasButtons: true,
    title: t("support.titleModalConfirmation"),
    text: t("support.subtitleModalConfirmation")
  }
})

const openModal = (object: ModalProps) => {
  modal.closeModal()
  modal.triggerModal(object)
}

const schema = yup.object({
  name: yup.string().required() as yup.StringSchema<string>,
  company_name: yup.string() as yup.StringSchema<string>,
  email: yup.string().email().required() as yup.StringSchema<string>,
  subject: yup.string().required() as yup.StringSchema<string>,
  explanation: yup.string() as yup.StringSchema<string>,
  files: yup.array().of(yup.mixed() as MixedSchema<File | string>)
})

const { errors, handleSubmit, isSubmitting } = useForm({
  validationSchema: schema,
  initialValues: {
    name: "",
    company_name: "",
    email: "",
    subject: subjectValues.value[0].value,
    explanation: "",
    files: []
  }
})
const { value: name } = useField<string>("name")
const { value: company_name } = useField<string>("company_name")
const { value: email } = useField<string>("email")
const { value: subject } = useField<string>("subject")
const { value: explanation } = useField<string>("explanation")
const isValid = useIsFormValid()

const onSubmit = handleSubmit(async (values, { resetForm }) => {
  const data = new FormData()
  data.append("name", values.name as string)
  data.append("company_name", values.company_name as string)
  data.append("email", values.email as string)
  data.append("subject", values.subject as string)
  data.append("explanation", values.explanation as string)
  selectedFiles.value.forEach(f => {
    data.append("files", f as Blob)
  })

  const response = await support.createSupport(data)

  if (response?.status === 200) {
    resetForm()
    openModal(modalStopOperation.value)
  }
})

const handleFileUpload = async () => {
  selectedFiles.value = [
    ...selectedFiles.value,
    ...(attachment.value.files as File[])
  ]
}

const removeFromselectedFiles = (index: number) => {
  selectedFiles.value = Array.from(selectedFiles.value).filter(
    (f, i) => i !== index
  )
}

const dropHandler = (ev: DragEvent) => {
  if (ev.dataTransfer?.items) {
    const list = [...ev.dataTransfer.items]
    list.forEach((item, i) => {
      if (item.kind === "file") {
        const file = item.getAsFile()
        if (file) {
          selectedFiles.value.push(file)
        }
      }
    })
  }
}
</script>

<template>
  <form @submit.prevent="onSubmit">
    <div class="flex flex-col">
      <div class="flex gap-4 justify-evenly">
        <div class="w-full">
          <Field
            id="name"
            :label="$t('support.nameSupport') + '*'"
            :feedback="errors.name"
          >
            <Input
              id="name"
              v-model="name"
              :placeholder="$t('support.placeholderName')"
              :feedback="errors.name"
            />
          </Field>
        </div>
        <div class="w-full">
          <Field
            id="company_name"
            :label="$t('support.companyName')"
            :feedback="errors.company_name"
          >
            <Input
              id="company_name"
              v-model="company_name"
              :placeholder="$t('support.placeholderCompanyName')"
              :feedback="errors.company_name"
            />
          </Field>
        </div>
      </div>
      <div class="flex gap-4 mt-6 justify-evenly">
        <div class="w-full">
          <Field
            id="email"
            :label="$t('support.emailSupport') + '*'"
            :feedback="errors.email"
          >
            <Input
              id="email"
              v-model="email"
              :placeholder="$t('support.placeholderEmail')"
              :feedback="errors.email"
            />
          </Field>
        </div>
        <div class="w-full mt-[0.5px]">
          <Field
            id="subject"
            :label="$t('support.subject') + '*'"
            :feedback="errors.subject"
          >
            <Select
              id="subject"
              v-model="subject"
              :items="subjectValues"
              :name="$t('support.placeholderSubject')"
              :has-label="true"
              :placeholder="$t('support.placeholderSubject')"
              :feedback="errors.subject"
            />
          </Field>
        </div>
      </div>
      <div class="flex gap-4 mt-6 justify-evenly">
        <div class="w-full">
          <Field id="explanation" :label="$t('support.explanation')">
            <Textarea
              id="explanation"
              v-model="explanation"
              :placeholder="$t('support.placeholderExplanation')"
            />
          </Field>
        </div>
      </div>
      <div class="flex gap-4 mt-6 justify-evenly">
        <div class="w-full">
          <Field id="files" :label="$t('support.attachment')" />
          <div class="mb-2 space-y-2">
            <div
              v-for="(file, index) in selectedFiles"
              :key="index"
              class="flex items-center justify-between p-3 bg-gray-700 rounded-md"
            >
              <div class="flex items-center gap-2">
                <PaperClipIcon class="w-5 h-5 text-gray-600" />
                <span>
                  {{ file["name"] }}
                </span>
              </div>
              <span
                class="cursor-pointer"
                @click="removeFromselectedFiles(index)"
              >
                <TrashIcon class="w-5 h-5 text-gray-400" />
              </span>
            </div>
          </div>
          <div
            class="flex items-center justify-center w-full"
            @drop.prevent="dropHandler"
            @dragover.prevent=""
          >
            <label
              for="files"
              class="flex flex-col items-center justify-center w-full border-2 border-gray-700 border-dashed rounded-lg cursor-pointer hover:bg-gray-900"
            >
              <div class="flex flex-col items-center justify-center pt-5 pb-6">
                <p class="flex gap-2 mt-2 mb-2 text-sm">
                  <svg
                    width="17"
                    height="18"
                    viewBox="0 0 17 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M10.643 4.83333L5.15482 10.3215C4.50394 10.9724 4.50394 12.0276 5.15482 12.6785C5.80569 13.3294 6.86097 13.3294 7.51184 12.6785L12.857 7.19036C14.1588 5.88861 14.1588 3.77806 12.857 2.47631C11.5553 1.17456 9.44472 1.17456 8.14297 2.47631L2.79779 7.96447C0.845173 9.91709 0.845173 13.0829 2.79779 15.0355C4.75042 16.9882 7.91624 16.9882 9.86886 15.0355L15.0833 9.83333"
                      stroke="#9CA3AF"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                  {{ $t("support.textAttachment") }}
                </p>
              </div>
              <input
                id="files"
                ref="attachment"
                type="file"
                class="hidden"
                accept="image/png, image/jpg, image/jpeg, image/pdf"
                multiple
                @change="handleFileUpload()"
              />
            </label>
          </div>
        </div>
      </div>
      <div class="pt-4">
        <Alert
          v-if="requestError.text"
          :type="requestError.type"
          :text="$t(requestError.text)"
          :timeout="requestError.timeout"
        />
      </div>
      <div class="flex items-center justify-between gap-4 mt-6">
        <LabelRequiredFields />
        <Button
          data-test-id="send-button"
          class="p-[10px]"
          type="submit"
          :icon-right="ChevronRightIcon"
          :text="$t('general.send')"
          :disabled="!isValid"
          :is-loading="isSubmitting"
        />
      </div>
    </div>
  </form>
</template>
